import { useLoginOptions } from '@contexts/login-options/login-options-context'
import { PrivyProvider } from '@privy-io/react-auth'
import { taurus } from '@utils/extra-chains'
import { ReactNode } from 'react'
import { defineChain } from 'viem'
import {
  anvil,
  arbitrum,
  avalanche,
  avalancheFuji,
  base,
  baseSepolia,
  bscTestnet,
  celoAlfajores,
  gnosis,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  polygonAmoy,
  polygonMumbai,
  scroll,
  scrollSepolia,
  sepolia,
  zkSync,
  zkSyncSepoliaTestnet,
} from 'viem/chains'

interface TSPrivyProviderProps {
  children: ReactNode
}

/** Privy's internal RPC for BSC is failing, so overriding here */
const bscOverride = defineChain({
  id: 56,
  name: 'BNB Smart Chain',
  nativeCurrency: {
    decimals: 18,
    name: 'BNB',
    symbol: 'BNB',
  },
  rpcUrls: {
    default: {
      http: [
        'https://bnb-mainnet.g.alchemy.com/v2/sN-ekw7W2tJwvEBWBUPMA1jqy9JWjrxM',
      ],
    },
  },
  blockExplorers: {
    default: {
      name: 'BscScan',
      url: 'https://bscscan.com',
      apiUrl: 'https://api.bscscan.com/api',
    },
  },
  contracts: {
    multicall3: {
      address: '0xca11bde05977b3631167028862be2a173976ca11',
      blockCreated: 15921452,
    },
  },
})

export const TSPrivyProvider = ({ children }: TSPrivyProviderProps) => {
  const { isEmailLoginEnabled, isWalletLoginEnabled } = useLoginOptions()

  // Dynamically determine login methods
  const loginMethods: ('email' | 'wallet')[] = []

  if (isEmailLoginEnabled) {
    loginMethods.push('email')
  }

  if (isWalletLoginEnabled) {
    loginMethods.push('wallet')
  }

  return (
    <PrivyProvider
      appId={`${import.meta.env.VITE_PRIVY_APP_ID}`}
      config={{
        loginMethods,
        appearance: {
          theme: 'light',
          accentColor: '#676FFF',
        },
        embeddedWallets: {
          createOnLogin: 'users-without-wallets',
        },
        defaultChain: sepolia,
        supportedChains: [
          arbitrum,
          avalanche,
          avalancheFuji,
          base,
          baseSepolia,
          bscOverride,
          bscTestnet,
          celoAlfajores,
          gnosis,
          mainnet,
          optimism,
          optimismSepolia,
          polygon,
          polygonAmoy,
          polygonMumbai,
          scroll,
          scrollSepolia,
          sepolia,
          zkSync,
          zkSyncSepoliaTestnet,
          anvil,
          taurus,
        ],
      }}
    >
      {children}
    </PrivyProvider>
  )
}
